/* ============================================================
   Limited Edition (redesign) controller.

   - Print product = Size-only variants (print-only price).
   - Frame = separate product (custom.frame_product) with Color + Size.
     Selecting a frame colour adds BOTH the print variant and the matching
     frame variant to the cart as two line items (linked by a shared _set id).
   - Per-size limit = variant metafield custom.edition_limit; sold = limit -
     inventory_quantity; next number = sold + 1 → "Collect № N".
   - Magnify: the gallery art scales between --le-min and --le-max as the
     selected size grows (theme-editor controlled).
   ============================================================ */
(function () {
  'use strict';

  function parse(el) { try { return JSON.parse(el.textContent); } catch (e) { return null; } }

  function formatMoney(cents, fmt) {
    var amount = (cents / 100);
    var withCommas = function (n, dp) {
      var parts = n.toFixed(dp).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };
    fmt = fmt || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas(amount, 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(amount, 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas(amount, 2))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommas(amount, 0))
      .replace(/<[^>]*>/g, '')            // strip any HTML in the shop money format
      .replace(/([.,])0{2}(?=\D|$)/, ''); // prints are whole dollars: drop a trailing .00 / ,00
  }

  var COLOUR_ORDER = ['natural', 'white', 'black'];
  var SWATCH = {
    unframed: 'repeating-linear-gradient(45deg,#fff,#fff 4px,#ededea 4px,#ededea 5px)',
    natural: 'linear-gradient(135deg,#d8b27e,#b98a52)',
    white: '#ffffff',
    black: '#111111'
  };

  function init(root) {
    if (root.__le2) return;
    root.__le2 = true;

    var print = parse(root.querySelector('[data-le2-print]'));
    var frameEl = root.querySelector('[data-le2-frame]');
    var frameProd = frameEl ? parse(frameEl) : null;
    var limits = parse(root.querySelector('[data-le2-limits]')) || {};
    var invMap = parse(root.querySelector('[data-le2-inv]')) || {};
    var moneyFmt = root.getAttribute('data-le2-money-format') || '${{amount}}';
    if (!print || !print.variants) return;

    // Global edition schedule (size name -> total), e.g. "Mini:150, Small:100".
    var schedule = {};
    (root.getAttribute('data-le2-schedule') || '').split(',').forEach(function (pair) {
      var kv = pair.split(':');
      if (kv.length === 2) {
        var n = parseInt(kv[1].replace(/[^0-9]/g, ''), 10);
        if (n > 0) schedule[kv[0].trim().toLowerCase()] = n;
      }
    });

    // Map LE size name -> frame block code (e.g. "Mini" -> "A4") so framing.js
    // resolves the right frame image + sizing for each size.
    var frameSizeMap = {};
    (root.getAttribute('data-le2-framesizemap') || '').split(',').forEach(function (pair) {
      var idx = pair.indexOf(':');
      if (idx > 0) frameSizeMap[pair.slice(0, idx).trim().toLowerCase()] = pair.slice(idx + 1).trim();
    });

    // Per-size display scale as a PERCENT (100 = actual size). e.g. "Large:115"
    // shows the Large frame 15% bigger. Stored as a 0..n multiplier.
    var scaleMap = {};
    (root.getAttribute('data-le2-scalemap') || '').split(',').forEach(function (pair) {
      var kv = pair.split(':');
      if (kv.length === 2) {
        var n = parseFloat(String(kv[1]).replace('%', ''));
        if (n > 0) scaleMap[kv[0].trim().toLowerCase()] = n / 100;
      }
    });

    // Size dimensions, like the default page: paper (unframed) vs framed, in
    // cm or inches per market. Keyed by size name (without any " - code" suffix).
    var unit = root.getAttribute('data-le2-unit') === 'in' ? 'in' : 'cm';
    var LE_DIMS = {
      'mini':      { paper: { cm: '21 × 29.7 cm',    in: '8.27 × 11.69 in' }, framed: { cm: '24 × 33 × 3 cm',     in: '9.4 × 13 × 1.2 in' } },
      'small':     { paper: { cm: '29.7 × 42 cm',    in: '11.69 × 16.54 in' }, framed: { cm: '33 × 45.4 × 3 cm',   in: '13 × 17.9 × 1.2 in' } },
      'medium':    { paper: { cm: '42 × 59.4 cm',    in: '16.54 × 23.39 in' }, framed: { cm: '46 × 63.5 × 4.5 cm', in: '18.1 × 25 × 1.8 in' } },
      'large':     { paper: { cm: '59.4 × 84.1 cm',  in: '23.39 × 33.11 in' }, framed: { cm: '63.5 × 88 × 4.5 cm',  in: '25 × 34.6 × 1.8 in' } },
      'x-large':   { paper: { cm: '70.7 × 100 cm',   in: '27.83 × 39.37 in' }, framed: { cm: '75 × 105 × 4.5 cm',   in: '29.5 × 41.3 × 1.8 in' } },
      'gallery':   { paper: { cm: '84.1 × 118.9 cm', in: '33.11 × 46.81 in' }, framed: { cm: '89 × 124 × 4.5 cm',   in: '35 × 48.8 × 1.8 in' } },
      'grand':     { paper: { cm: '101.6 × 143.7 cm', in: '40 × 56.57 in' },   framed: { cm: '106 × 157 × 4.5 cm',  in: '41.7 × 61.8 × 1.8 in' } },
      'collector': { paper: { cm: '127 × 179.6 cm',  in: '50 × 70.71 in' },    framed: null },
      'statement': { paper: { cm: '152.4 × 215.5 cm', in: '60 × 84.84 in' },   framed: null }
    };
    // Size key: strip a trailing " - code" (e.g. "Mini - A4" -> "mini") but
    // keep internal hyphens so "X-Large" stays intact.
    function dimKey(name) { return String(name == null ? '' : name).split(/\s+[-–—]\s+/)[0].trim().toLowerCase(); }
    function sizeDims(name) {
      var e = LE_DIMS[dimKey(name)];
      if (!e) return '';
      var framed = state.colour && state.colour !== 'unframed';
      var set = (framed && e.framed) ? e.framed : e.paper;
      var out = set[unit] || set.cm || '';
      // Drop the depth (third dimension) from framed sizes so the selector shows
      // width × height only: "24 × 33 × 3 cm" → "24 × 33 cm".
      out = out.replace(/^(\s*[\d.]+ × [\d.]+) × [\d.]+ (cm|in)\s*$/, '$1 $2');
      return out;
    }


    var section = root.closest('.pdp-redesign') || root.closest('[data-section-type="product"]');

    // normalise a size label so "Mini - A4" (print) matches "Mini" (frame).
    // Strip a " - code/orientation" suffix (e.g. "Medium - A2" -> "medium") but
    // keep hyphenated size NAMES intact — the separator must have whitespace on
    // both sides, otherwise "X-Large" would collapse to just "x".
    function normSize(s) { return String(s == null ? '' : s).split(/\s+[-–—]\s+/)[0].trim().toLowerCase(); }

    // ---- sizes: dedupe by the Size option; representative = cheapest
    // (print-only) variant for that size. Works whether the print product is
    // Size-only or still carries a Frame option. ----
    var optNames = (print.options || []).map(function (o) {
      return (typeof o === 'string' ? o : (o && o.name) || '').toLowerCase();
    });
    var sizeIdx = optNames.indexOf('size'); if (sizeIdx < 0) sizeIdx = 0;
    function sizeOf(v) {
      if (v.options && v.options[sizeIdx] != null) return v.options[sizeIdx];
      return v['option' + (sizeIdx + 1)];
    }

    var order = [], groups = {};
    print.variants.forEach(function (v) {
      var sv = sizeOf(v);
      if (!(sv in groups)) { groups[sv] = v; order.push(sv); }
      else if (v.price < groups[sv].price) { groups[sv] = v; }
    });

    var sizes = order.map(function (sv) {
      var v = groups[sv];
      var inv = invMap[v.id] || {};
      var managed = !!inv.m;
      return {
        id: v.id,
        name: sv,
        price: v.price,
        available: (typeof inv.a === 'boolean') ? inv.a : v.available,
        managed: managed,
        qty: managed ? (typeof inv.q === 'number' ? inv.q : null) : null,
        limit: parseInt(limits[v.id], 10) || 0
      };
    });

    // ---- frame colours from the frame product ----
    var frameColorIdx = -1, frameSizeIdx = -1;
    if (frameProd && frameProd.options) {
      frameProd.options.forEach(function (n, i) {
        var ln = String(n).toLowerCase();
        if (ln === 'color' || ln === 'colour') frameColorIdx = i;
        if (ln === 'size') frameSizeIdx = i;
      });
    }
    function frameOpt(v, idx) { return idx >= 0 ? v['option' + (idx + 1)] : null; }

    var colours = [];
    if (frameProd && frameColorIdx >= 0) {
      var seen = {};
      frameProd.variants.forEach(function (v) {
        var c = frameOpt(v, frameColorIdx);
        if (c && !seen[c.toLowerCase()]) { seen[c.toLowerCase()] = 1; colours.push(c); }
      });
      colours.sort(function (a, b) {
        var ia = COLOUR_ORDER.indexOf(a.toLowerCase()), ib = COLOUR_ORDER.indexOf(b.toLowerCase());
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
    }
    var frameOptions = [{ id: 'unframed', label: 'Unframed' }].concat(
      colours.map(function (c) { return { id: c.toLowerCase(), label: c, value: c }; })
    );

    function frameVariantFor(colourId, sizeName) {
      if (!frameProd || colourId === 'unframed') return null;
      return frameProd.variants.filter(function (v) {
        var c = (frameOpt(v, frameColorIdx) || '').toLowerCase();
        var s = normSize(frameOpt(v, frameSizeIdx));
        return c === colourId && s === normSize(sizeName);
      })[0] || null;
    }

    // ---- state ----
    var state = { sizeIndex: 0, colour: 'natural', open: false };
    // Default size: Medium if available, otherwise the first in-stock size.
    for (var i = 0; i < sizes.length; i++) { if (sizes[i].available) { state.sizeIndex = i; break; } }
    for (var i = 0; i < sizes.length; i++) { if (normSize(sizes[i].name) === 'medium' && sizes[i].available) { state.sizeIndex = i; break; } }

    // Deep-link: honour ?variant= (art size variant) and ?frame= (colour) from
    // the URL so a shared link reopens the same selection.
    (function () {
      var qs = new URLSearchParams(window.location.search);
      var vid = parseInt(qs.get('variant'), 10);
      if (vid) { for (var i = 0; i < sizes.length; i++) { if (sizes[i].id === vid) { state.sizeIndex = i; break; } } }
      var fr = (qs.get('frame') || '').toLowerCase();
      if (fr === 'unframed' || fr === 'natural' || fr === 'white' || fr === 'black') state.colour = fr;
    })();

    var els = {
      eyebrow: root.querySelector('[data-le2-eyebrow]'),
      size: root.querySelector('[data-le2-size]'),
      counter: root.querySelector('[data-le2-counter]'),
      frames: root.querySelector('[data-le2-frames]'),
      note: root.querySelector('[data-le2-note]'),
      edBox: root.querySelector('[data-le2-edition]'),
      edBadge: root.querySelector('[data-le2-ed-badge]'),
      edTitle: root.querySelector('[data-le2-ed-title]'),
      edYours: root.querySelector('[data-le2-ed-yours]'),
      edFill: root.querySelector('[data-le2-ed-fill]'),
      atc: root.querySelector('[data-le2-atc]'),
      atcLabel: root.querySelector('[data-le2-atc-label]'),
      atcPrice: root.querySelector('[data-le2-atc-price]'),
      marqueeTrack: root.querySelector('[data-le2-marquee-track]'),
      sizeSelect: root.querySelector('[data-le2-size-select]')
    };

    function curSize() { return sizes[state.sizeIndex]; }
    function frameVar() { return frameVariantFor(state.colour, curSize().name); }
    function totalPrice() {
      var fv = frameVar();
      return curSize().price + (fv ? fv.price : 0);
    }
    // Effective edition total: edition_limit metafield, else current inventory
    // (so the counter shows before the metafield is filled in).
    function effLimit(s) {
      if (s.limit > 0) return s.limit;                 // per-variant metafield wins
      var sch = schedule[normSize(s.name)];            // global schedule by size name
      if (sch) return sch;
      return s.qty != null ? s.qty : 0;                // last resort: current stock
    }
    function soldInfo() {
      var s = curSize();
      var lim = effLimit(s);
      if (!lim || s.qty == null) return null;
      var sold = Math.max(0, lim - s.qty);
      return { sold: sold, next: sold + 1, qty: s.qty, limit: lim };
    }

    function renderEyebrow() {
      var info = soldInfo();
      els.eyebrow.textContent = info ? ('№ ' + info.next + ' / ' + info.limit) : '';
    }

    // Total shown per size row: print + the currently selected frame (0 when
    // Unframed), so the dropdown price matches the Collect button.
    function totalForSize(s) {
      var fv = frameVariantFor(state.colour, s.name);
      return s.price + (fv ? fv.price : 0);
    }

    // Build the size rows with the DEFAULT page's fo-size classes so they look
    // identical to the standard product page.
    function renderSize() {
      els.size.className = 'fo-size__rows' + (state.open ? ' fo-size--open' : '');
      els.size.innerHTML = sizes.map(function (s, i) {
        var on = i === state.sizeIndex;
        var dis = !s.available;
        var ed = effLimit(s);
        return '<button type="button" class="fo-size-row" aria-checked="' + (on ? 'true' : 'false') + '" data-le2-size-row="' + i + '"' + (dis ? ' disabled' : '') + '>'
          + '<span class="fo-size-row__namecol">'
          +   '<span class="fo-size-row__name">' + s.name + '</span>'
          +   (ed ? '<span class="fo-size-row__edition">Edition of ' + ed + '</span>' : '')
          + '</span>'
          + '<span class="fo-size-row__dims">' + sizeDims(s.name) + '</span>'
          + '<span class="fo-size-row__price">' + formatMoney(totalForSize(s), moneyFmt) + '</span>'
          + '<span class="fo-size-row__chev" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="#111111" stroke-width="1"><path d="M6 9l6 6 6-6"></path></svg></span>'
          + '</button>';
      }).join('');
      // The collapsed row is the trigger for the size-picker sheet (all widths).
      var trg = els.size.querySelector('.fo-size-row[aria-checked="true"]');
      if (trg) {
        trg.setAttribute('aria-haspopup', 'listbox');
        trg.setAttribute('aria-expanded', sheet.open ? 'true' : 'false');
      }
      els.size.querySelectorAll('[data-le2-size-row]').forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.hasAttribute('disabled')) return;
          openSheet();
        });
      });
    }

    function renderCounter() {
      // Edition info now lives in the boxed container above the button
      // (renderNote); the under-dropdown counter is hidden.
      els.counter.hidden = true;
    }

    // chip class used by framing-options.css for each colour.
    function chipKind(id) {
      if (id === 'unframed') return 'none';
      if (id === 'natural' || id === 'white' || id === 'black') return id;
      return 'other';
    }

    // Is a frame colour offered for the current size? Unframed always; a colour
    // only if the Frame product has an (available) variant for that size.
    function colourAvailable(id) {
      if (id === 'unframed') return true;
      var fv = frameVariantFor(id, curSize().name);
      return !!(fv && fv.available !== false);
    }

    // Build frame swatches with the DEFAULT page's fo-frame-swatch classes.
    // Only colours available for the current size are rendered.
    function renderFrames() {
      els.frames.className = 'fo-frame__swatches';
      els.frames.innerHTML = frameOptions.filter(function (f) {
        return colourAvailable(f.id);
      }).map(function (f) {
        var on = state.colour === f.id;
        return '<button type="button" class="fo-frame-swatch" aria-checked="' + (on ? 'true' : 'false') + '" data-le2-frame-opt="' + f.id + '">'
          + '<span class="fo-frame-swatch__tile"><span class="fo-frame-swatch__chip fo-frame-swatch__chip--' + chipKind(f.id) + '"></span></span>'
          + '<span class="fo-frame-swatch__label">' + f.label + '</span>'
          + '</button>';
      }).join('');
      els.frames.querySelectorAll('[data-le2-frame-opt]').forEach(function (b) {
        b.addEventListener('click', function () {
          state.colour = b.getAttribute('data-le2-frame-opt');
          render();
          // Return to the first (framed) slide on a frame change, like size does.
          gotoFirstSlide();
          // Also notify the mobile gallery slider (Flickity) — it resets on any
          // change bubbling out of .le2.
          els.frames.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    function renderEdition() {
      if (!els.edBox) return;
      var total = effLimit(curSize());
      if (!total) { els.edBox.hidden = true; return; }
      els.edBox.hidden = false;
      if (els.edBadge) els.edBadge.textContent = total;
      if (els.edTitle) els.edTitle.textContent = 'Limited Edition of ' + total;
      var info = soldInfo();
      if (info && els.edYours) {
        els.edYours.textContent = 'You will collect № ' + info.next + ' of ' + total;
        if (els.edFill) els.edFill.style.width = Math.max(2, Math.min(100, info.sold / total * 100)) + '%';
      } else if (els.edYours) {
        els.edYours.textContent = '';
        if (els.edFill) els.edFill.style.width = '0%';
      }
    }

    function renderMarquee() {
      if (!els.marqueeTrack) return;
      var info = soldInfo();
      var total = effLimit(curSize());
      if (!info || !total) { els.marqueeTrack.innerHTML = ''; els.marqueeTrack.style.animationDuration = ''; return; }
      var one = '';
      for (var i = 0; i < 12; i++) {
        one += '<span>Collect № ' + info.next + ' of ' + total + '</span>';
        one += '<span>Free Shipping in Australia</span>';
      }
      els.marqueeTrack.innerHTML = one + one; // duplicated for a seamless -50% loop
      // Keep the original scroll speed (px/sec) regardless of content width.
      var firstSpan = els.marqueeTrack.querySelector('span');
      var collectWidth = firstSpan ? firstSpan.getBoundingClientRect().width : 0;
      var half = els.marqueeTrack.scrollWidth / 2;
      if (collectWidth > 0 && half > 0) {
        els.marqueeTrack.style.animationDuration = (half / (collectWidth / 3)) + 's';
      }
    }

    function renderAtc() {
      var s = curSize();
      var info = soldInfo();
      var soldOut = s.available === false || (s.qty != null && s.qty <= 0);
      if (soldOut) {
        els.atcLabel.textContent = 'Sold out';
        els.atcPrice.textContent = '';
        els.atc.disabled = true;
        els.atc.classList.add('is-sold');
        return;
      }
      els.atc.disabled = false;
      els.atc.classList.remove('is-sold');
      els.atcLabel.textContent = 'Add to cart';
      els.atcPrice.textContent = formatMoney(totalPrice(), moneyFmt);
    }

    // ---- layered frame preview (House of Spoils style) ----
    // The first gallery slide becomes a clean stage: artwork layer + a
    // transparent frame-overlay PNG on top. We read the overlay URLs + window
    // inset straight from FrameImgContainer (populated by the LE frame sections).
    var artUrl = root.getAttribute('data-le2-art') || '';

    function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
    function galleryOrient() {
      var mf = document.querySelector('.le-template .product-gallery__carousel-item.main-frame');
      var o = mf && mf.getAttribute('data-frame-oriantation');
      if (o === 'H') return 'H';
      if (o === 'S') return 'S';
      return 'V';
    }
    // Accept BOTH the new size-name handles ("Medium - V") and the legacy
    // codes ("A2-V", "40-V"…). Each size maps to its name + legacy aliases.
    var LE_ALIASES = {
      'mini': ['mini', 'a4'],
      'small': ['small', 'a3'],
      'medium': ['medium', 'a2'],
      'large': ['large', 'a1'],
      'x-large': ['x-large', 'b1'],
      'gallery': ['gallery', 'a0'],
      'grand': ['grand', '40', '40x60'],
      'collector': ['collector'],
      'statement': ['statement']
    };
    function normHandle(s) { return String(s == null ? '' : s).toLowerCase().replace(/\s*-\s*/g, '-').trim(); }
    // strip spaces + unit words so "40x60 inches" == "40x60", "x large" == "xlarge".
    function normSizePart(s) { return String(s == null ? '' : s).toLowerCase().replace(/inches?|cm/g, '').replace(/\s+/g, ''); }

    function frameData() {
      var fic = window.FrameImgContainer || {};
      // Unframed uses its own mat overlay (LEUnframed); if none is set up it
      // falls back to the bare print (null).
      var ficKey = state.colour === 'unframed' ? 'LEUnframed_frame' : 'LE' + cap(state.colour) + '_frame';
      var arr = (fic[ficKey] || []).filter(function (b) {
        // ignore blocks with no image uploaded (Shopify no-image placeholder)
        return b.size_url && b.size_url.indexOf('no-image') < 0;
      });
      if (!arr.length) return null;

      var sn = normSize(curSize().name);                 // "medium"
      var orient = galleryOrient().toLowerCase();        // "v" / "h" / "s"
      var accept = (LE_ALIASES[sn] || [sn]).slice();     // name + legacy codes
      var mapped = frameSizeMap[sn];                      // from the theme setting
      if (mapped) accept.push(mapped);
      accept = accept.map(normSizePart);

      return arr.filter(function (b) {
        var parts = normHandle(b.size_handle).split('-');
        var ho = parts.pop();                            // orientation token
        if (ho !== orient) return false;
        return accept.indexOf(normSizePart(parts.join('-'))) >= 0;
      })[0] || null;
    }

    // The art window inset. Primary control is "Print size" (print_scale): a
    // single % of the frame. Because the overlay canvas is square and the print
    // keeps its own aspect (object-fit: contain), an equal inset on all sides
    // auto-centers it for any orientation. A non-blank manual padding_ratio
    // overrides it (for off-centre openings).
    function framePadding(fd) {
      var manual = (fd && fd.padding_ratio ? String(fd.padding_ratio) : '').trim();
      if (manual && manual.indexOf('%') >= 0) return manual;
      var scale = parseFloat(fd && fd.print_scale);
      if (!isNaN(scale) && scale > 0 && scale < 100) {
        var m = (100 - scale) / 2;
        return m + '% ' + m + '% ' + m + '% ' + m + '%';
      }
      return '14% 14% 14% 14%'; // sensible default (~72% print)
    }

    // Tell the theme's Flickity gallery to re-measure (it sets viewport height
    // from the cell, which can be stale after we inject/resize the stage).
    function resizeGallery() {
      // Flickity's viewport height is measured from the selected cell; after we
      // change the stage it can be stale. A window 'resize' triggers Flickity's
      // own resize handler reliably (no need to reach the private instance).
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
      var car = document.querySelector('.le-template .product-gallery__carousel');
      if (car && window.Flickity && window.Flickity.data) {
        var fl = window.Flickity.data(car);
        if (fl && fl.resize) { try { fl.resize(); } catch (e) {} }
      }
    }

    // Jump the gallery back to the first slide (the framed artwork). Navigates
    // the same way the scrubber does — by clicking the hidden thumbnail for the
    // first carousel item's media — so it works without the Flickity instance.
    function gotoFirstSlide() {
      var gallery = document.querySelector('.le-template .product-gallery');
      if (!gallery) return;
      var first = gallery.querySelector('.product-gallery__carousel-item');
      if (!first) return;
      var id = first.getAttribute('data-media-id');
      var thumb = id && gallery.querySelector('.product-gallery__thumbnail[data-media-id="' + id + '"]');
      if (thumb) thumb.click();
    }

    // Drive the THEME's existing frame structure (frame-img overlay + the art
    // inside .padding-main-art-container) instead of replacing the cell. This
    // keeps the cell Flickity already measured, so there's no height race when
    // there are multiple gallery images.
    function renderStage() {
      var mf = document.querySelector('.le-template .product-gallery__carousel-item.main-frame');
      if (!mf) return;
      var frameImg = mf.querySelector('img.frame-img');
      var artImg = mf.querySelector('img.main-art-img');
      var pad = mf.querySelector('.padding-main-art-container');
      var fcont = mf.querySelector('.frame-container');
      var ar = mf.querySelector('.aspect-ratio');
      if (!frameImg) return;

      // The theme sizes the gallery box from the ARTWORK aspect (portrait).
      // Our frame overlays are square PNGs, so in that portrait box the square
      // frame only fills the top and leaves a gap below. Match the box to the
      // displayed image's real aspect: square for overlays, art aspect when
      // unframed. Capture the theme's original (art) padding once to restore it.
      if (ar && !ar.getAttribute('data-le-base-pb')) {
        ar.setAttribute('data-le-base-pb', ar.style.paddingBottom || '');
      }
      function fitBoxTo(img) {
        if (!ar) return;
        if (img.naturalWidth && img.naturalHeight) {
          ar.style.paddingBottom = (img.naturalHeight / img.naturalWidth * 100) + '%';
        }
        resizeGallery();
      }
      function restoreBox() {
        if (ar) ar.style.paddingBottom = ar.getAttribute('data-le-base-pb') || '';
      }

      var fd = frameData();
      if (fd) {
        // Framed: the overlay PNG fills the frame slot (gives the cell its
        // height); the print sits in the window via padding.
        if (frameImg.getAttribute('src') !== fd.size_url) {
          frameImg.onload = function () { fitBoxTo(frameImg); };
          frameImg.src = fd.size_url;
        } else {
          fitBoxTo(frameImg);
        }
        frameImg.classList.remove('not-visible');
        if (fcont) fcont.classList.remove('frame-not-visible');
        if (pad) { pad.style.display = ''; pad.style.padding = framePadding(fd); }
        if (artImg && artUrl && artImg.getAttribute('src') !== artUrl) artImg.src = artUrl;
      } else {
        // Unframed: show the bare print IN the frame slot so the cell still has
        // height (an empty frame-img is 0px tall and collapses the gallery).
        restoreBox();
        if (artUrl && frameImg.getAttribute('src') !== artUrl) { frameImg.onload = resizeGallery; frameImg.src = artUrl; }
        frameImg.classList.remove('not-visible');
        if (fcont) fcont.classList.remove('frame-not-visible');
        if (pad) pad.style.display = 'none';
      }
      resizeGallery();
    }

    function render() {
      // If the chosen frame colour isn't offered for the current size (e.g.
      // moving to Statement, which has no frames), fall back to an available one.
      if (!colourAvailable(state.colour)) {
        var alt = frameOptions.filter(function (f) { return colourAvailable(f.id); });
        state.colour = alt.length ? alt[0].id : 'unframed';
      }
      renderEyebrow(); renderSize(); renderCounter(); renderFrames(); renderEdition(); renderAtc(); renderMarquee(); renderStage();
      syncUrl();
    }

    // Reflect the current selection in the URL (?variant=<size>&frame=<colour>)
    // via replaceState — makes each variant shareable/linkable without adding
    // browser-history entries.
    function syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      var qs = new URLSearchParams(window.location.search);
      qs.set('variant', curSize().id);
      qs.set('frame', state.colour);
      window.history.replaceState({}, '', window.location.pathname + '?' + qs.toString() + window.location.hash);
    }

    // ---- add to cart (print + frame as separate line items) ----
    els.atc.addEventListener('click', function () {
      if (els.atc.disabled) return;
      var info = soldInfo();
      var setId = 'le-' + Date.now();
      var items = [{
        id: curSize().id,
        quantity: 1,
        properties: info ? { _set: setId, 'Edition': '№ ' + info.next } : { _set: setId }
      }];
      var fv = frameVar();
      if (fv) {
        items.push({ id: fv.id, quantity: 1, properties: { _set: setId, 'Frame for': print.title } });
      }
      els.atc.disabled = true;
      els.atcLabel.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error('cart');
        window.location.href = '/cart';
      }).catch(function () {
        els.atc.disabled = false;
        render();
        alert('Sorry — could not add to cart. Please try again.');
      });
    });

    // ===== Mobile bottom-sheet size picker (design_handoff_size_picker_sheet) =====
    // On phones the inline size list is replaced by a bottom sheet that covers
    // the lower half of the viewport, leaving the artwork visible above. A
    // Classic / Larger segmented control keeps the visible list to six rows.
    var REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
    var DESKTOP = window.matchMedia('(min-width: 768px)');
    var CLASSIC_COUNT = Math.min(6, sizes.length);
    var HAS_LARGER = sizes.length > CLASSIC_COUNT;
    var sheet = { el: null, list: null, group: 'classic', open: false, scrollY: 0, lastFocus: null };

    function groupOf(idx) { return idx < CLASSIC_COUNT ? 'classic' : 'larger'; }
    function groupRange(g) { return g === 'larger' ? [CLASSIC_COUNT, sizes.length] : [0, CLASSIC_COUNT]; }

    // The muted "Framing: <colour>" line under the sheet heading. Reflects the
    // frame colour selected on the page; never shows the "shadow box" wording.
    function frameLabel() {
      for (var i = 0; i < frameOptions.length; i++) {
        if (frameOptions[i].id === state.colour) return frameOptions[i].label;
      }
      return '';
    }
    function renderFramingSub() {
      if (!sheet.panel) return;
      var el = sheet.panel.querySelector('[data-le2sheet-sub]');
      if (!el) return;
      var lbl = frameLabel().replace(/\s*shadow\s*box/i, '').trim();
      el.textContent = lbl ? ('Framing: ' + lbl) : '';
    }

    // Desktop: the picker opens INLINE inside the size block, pushing the rest
    // of the column (Framing, Add to Cart…) down — not as an overlay. Move the
    // panel into .fo-size and hide the collapsed trigger while open.
    function foSizeBlock() { return els.size.closest('.fo-size'); }
    function placeInline() {
      var panel = document.querySelector('.le2sheet__panel');
      if (!panel) return;
      panel.classList.add('le2sheet__panel--inline');
      panel.style.top = panel.style.left = panel.style.width = panel.style.maxHeight = '';
      var fo = foSizeBlock();
      if (fo) { fo.classList.add('fo-size--picking'); fo.appendChild(panel); }
      sheet.inline = true;
    }
    function unplaceInline() {
      var panel = document.querySelector('.le2sheet__panel');
      if (panel) {
        panel.classList.remove('le2sheet__panel--inline');
        if (sheet.el && panel.parentNode !== sheet.el) sheet.el.appendChild(panel); // return it home
      }
      var fo = foSizeBlock();
      if (fo) fo.classList.remove('fo-size--picking');
      sheet.inline = false;
    }
    function panelIsInline() { return !!sheet.inline; }
    // Resizing across the phone/desktop boundary while open just closes it.
    function onResizeWhileOpen() { if (sheet.open) closeSheet(); }

    function buildSheet() {
      if (sheet.el) return;
      var seg = HAS_LARGER
        ? '<div class="le2sheet__seg" role="tablist" aria-label="Size group">'
          + '<button type="button" class="le2sheet__seg-btn" role="tab" data-le2sheet-group="classic"></button>'
          + '<button type="button" class="le2sheet__seg-btn" role="tab" data-le2sheet-group="larger"></button>'
          + '</div>'
        : '';
      var wrap = document.createElement('div');
      wrap.className = 'le2sheet';
      wrap.setAttribute('data-le2sheet', '');
      wrap.hidden = true;
      wrap.innerHTML =
        '<div class="le2sheet__scrim" data-le2sheet-scrim></div>'
        + '<div class="le2sheet__panel" role="dialog" aria-modal="true" aria-label="Print size">'
        +   '<div class="le2sheet__head">'
        +     '<div class="le2sheet__headtext">'
        +       '<span class="le2sheet__title">Select your size</span>'
        +       '<span class="le2sheet__sub" data-le2sheet-sub></span>'
        +     '</div>'
        +     '<button type="button" class="le2sheet__close" data-le2sheet-close aria-label="Close">'
        +       '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#111111" stroke-width="1" aria-hidden="true"><path d="M5 5l14 14"></path><path d="M19 5L5 19"></path></svg>'
        +     '</button>'
        +   '</div>'
        +   seg
        +   '<div class="le2sheet__list" data-le2sheet-list role="listbox" aria-label="Print size" tabindex="-1"></div>'
        + '</div>';
      document.body.appendChild(wrap);
      sheet.el = wrap;
      sheet.panel = wrap.querySelector('.le2sheet__panel'); // moves out of .el when inline
      sheet.list = wrap.querySelector('[data-le2sheet-list]');
      wrap.querySelector('[data-le2sheet-scrim]').addEventListener('click', function () {
        // Ignore a click-through / ghost tap right after opening (the tap that
        // opened the sheet can otherwise land on the freshly-shown scrim and
        // immediately close it).
        if (Date.now() - (sheet.openedAt || 0) < 350) return;
        closeSheet();
      });
      wrap.querySelector('[data-le2sheet-close]').addEventListener('click', closeSheet);
      wrap.querySelectorAll('[data-le2sheet-group]').forEach(function (b) {
        b.addEventListener('click', function () { setGroup(b.getAttribute('data-le2sheet-group')); });
      });
      wrap.addEventListener('keydown', onSheetTab);
    }

    function renderSeg() {
      if (!HAS_LARGER || !sheet.panel) return;
      var largerCount = sizes.length - CLASSIC_COUNT;
      sheet.panel.querySelectorAll('[data-le2sheet-group]').forEach(function (b) {
        var g = b.getAttribute('data-le2sheet-group');
        var active = sheet.group === g;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        // The count suffix appears on the INACTIVE side only, as a hint of what
        // is behind the tap.
        if (g === 'classic') b.textContent = active ? 'Classic' : ('Classic · ' + CLASSIC_COUNT);
        else b.textContent = active ? 'Larger' : ('Larger · ' + largerCount);
      });
    }

    function renderSheetList() {
      if (!sheet.list) return;
      var range = groupRange(sheet.group);
      var html = '';
      for (var i = range[0]; i < range[1]; i++) {
        var s = sizes[i];
        var ed = effLimit(s);
        var on = i === state.sizeIndex;
        var dis = !s.available;
        html += '<button type="button" class="le2sheet__row' + (on ? ' is-selected' : '') + '" role="option" aria-selected="' + (on ? 'true' : 'false') + '" data-le2sheet-row="' + i + '"' + (dis ? ' disabled' : '') + '>'
          + '<span class="le2sheet__namecol">'
          +   '<span class="le2sheet__name">' + s.name + '</span>'
          +   (ed ? '<span class="le2sheet__ed">Edition of ' + ed + '</span>' : '')
          + '</span>'
          + '<span class="le2sheet__dims">' + sizeDims(s.name) + '</span>'
          + '<span class="le2sheet__price">' + formatMoney(totalForSize(s), moneyFmt) + '</span>'
          + '</button>';
      }
      sheet.list.innerHTML = html;
      sheet.list.querySelectorAll('[data-le2sheet-row]').forEach(function (b) {
        b.addEventListener('click', function () {
          if (b.hasAttribute('disabled')) return;
          state.sizeIndex = parseInt(b.getAttribute('data-le2sheet-row'), 10);
          if (els.sizeSelect) { els.sizeSelect.value = curSize().name; els.sizeSelect.dispatchEvent(new Event('change', { bubbles: true })); }
          closeSheet();
          render();
          gotoFirstSlide();
        });
      });
      renderSeg();
    }

    function setGroup(g) {
      if (sheet.group === g) return;
      sheet.group = g;
      renderSheetList();
      if (sheet.list) sheet.list.scrollTop = 0;
    }

    function lockBodyScroll() {
      // Lock via overflow — keeps the exact scroll position, so tapping the
      // trigger never repositions the page. (The old position:fixed + top:-Y
      // trick jumped on mobile — esp. iOS with the address bar — and could eat
      // the first tap, needing a second click.)
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    function unlockBodyScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    function openSheet() {
      buildSheet();
      sheet.group = groupOf(state.sizeIndex);   // open on the selected size's group
      renderSheetList();
      renderFramingSub();
      sheet.lastFocus = document.activeElement;
      sheet.open = true;
      sheet.openedAt = Date.now();

      if (DESKTOP.matches) {
        // Inline dropdown: pushes the column down, no scrim / scroll-lock.
        placeInline();
      } else {
        // Mobile bottom sheet overlay.
        lockBodyScroll();
        document.documentElement.classList.add('le2sheet-lock'); // hides the chat launcher
        sheet.el.hidden = false;
        void sheet.el.offsetHeight;              // force reflow so the transition runs
        sheet.el.classList.add('is-open');
      }

      window.addEventListener('resize', onResizeWhileOpen);
      var trig = els.size.querySelector('.fo-size-row[aria-checked="true"]');
      if (trig) trig.setAttribute('aria-expanded', 'true');
      var closeBtn = document.querySelector('[data-le2sheet-close]');
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onDocEsc, true);
    }

    function closeSheet() {
      if (!sheet.open) return;
      sheet.open = false;
      document.removeEventListener('keydown', onDocEsc, true);
      window.removeEventListener('resize', onResizeWhileOpen);
      var trig = els.size.querySelector('.fo-size-row[aria-checked="true"]');
      if (trig) trig.setAttribute('aria-expanded', 'false');

      if (panelIsInline()) {
        // Desktop inline: restore the trigger and return the panel home.
        unplaceInline();
        if (sheet.lastFocus && sheet.lastFocus.focus) { try { sheet.lastFocus.focus(); } catch (e) {} }
        return;
      }

      // Mobile bottom sheet: slide down, then tidy up.
      sheet.el.classList.remove('is-open');
      var finish = function () {
        sheet.el.hidden = true;
        unlockBodyScroll();
        document.documentElement.classList.remove('le2sheet-lock');
        if (sheet.lastFocus && sheet.lastFocus.focus) { try { sheet.lastFocus.focus(); } catch (e) {} }
      };
      if (REDUCE_MOTION.matches) { finish(); return; }
      var panel = sheet.el.querySelector('.le2sheet__panel');
      var done = false;
      var onEnd = function () { if (done) return; done = true; panel.removeEventListener('transitionend', onEnd); finish(); };
      panel.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, 320);                     // fallback if transitionend never fires
    }

    function onDocEsc(e) {
      if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); closeSheet(); }
    }
    function onSheetTab(e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var f = sheet.el.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    render();
    // FrameImgContainer is populated by inline scripts later in the page, so
    // re-render the stage a few times until the overlay URLs are available.
    [150, 600, 1500].forEach(function (d) { setTimeout(renderStage, d); });
    window.addEventListener('load', renderStage);

    // Mobile: show the eyebrow/title/artist above the image, like the default
    // product page. Move the header before the gallery on small screens and
    // back into the info panel on desktop.
    (function () {
      var head = root.querySelector('[data-le2-head]');
      if (!head) return;
      var home = head.parentNode;                 // its normal spot (top of .le2)
      // Full-width context above the whole product block (the .container),
      // so the header isn't confined to the half-width info-panel wrapper.
      var blockList = document.querySelector('.le-template .product-block-list');
      var container = blockList ? blockList.parentNode : null;
      head.classList.add('le2__head--mobile-ready');
      var mq = window.matchMedia('(max-width: 999px)');
      function place() {
        if (mq.matches) {
          if (container && blockList && head.parentNode !== container) {
            container.insertBefore(head, blockList);
            head.classList.add('le2__head--floated');
          }
        } else if (home && head.parentNode !== home) {
          home.insertBefore(head, home.firstChild);
          head.classList.remove('le2__head--floated');
        }
      }
      place();
      if (mq.addEventListener) mq.addEventListener('change', place);
      else if (mq.addListener) mq.addListener(place);
    })();
  }

  function boot() {
    document.querySelectorAll('[data-le2]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
